import React, { useState, useRef, useCallback, useEffect } from 'react';
import Icon from '../icon/Icon';
import P from '../texts/p/P';
import DropDown from '../dropdown/DropDown';
import Spinner from '../Spinner/spinner';
import Span from '../texts/span/Span';
import Card from '../card/Card';
import Label from '../texts/label/Label';
import { useClientTheme } from '../../context/ThemeContext';
import TruncatedTextWithTooltip from '../TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import {
  BulkUploaderWrapper,
  DragAndDropArea,
  DropBaseIcon,
  DropBaseIconWrapper,
  MainTextWrapper,
  FileInput,
  GlobalError,
  FileRowContainer,
  DropdownWrapper,
  FileNameWrapper,
  FileNameLabelWrapper,
} from './BulkDocumentUploader.styles';
import {
  BulkDocumentUploaderProps,
  FileWithType,
  UploadStatus,
} from './BulkDocumentUploader.types';
import {
  validateFile,
  validateDuplicateFiles,
  generateFileId,
} from './BulkDocumentUploader.utils';
import {
  ACCEPTED_FORMATS,
  ACCEPTED_EXTENSIONS,
  MAX_FILE_SIZE,
  BULK_UPLOAD_ERROR_MESSAGES,
} from '../../utils/Constants';
import { saveClientDocument } from '../../services/ClientService';
import { removeFileExtension } from '../../utils/Utils';

const BulkDocumentUploader: React.FC<BulkDocumentUploaderProps> = ({
  documentTypes,
  onFilesUploaded,
  onValidationChange,
  onError,
  maxFileSize = MAX_FILE_SIZE,
  acceptedFormats = ACCEPTED_FORMATS,
  acceptedExtensions = ACCEPTED_EXTENSIONS,
  disabled = false,
  cuit,
  onFileUpload,
  deferredUpload = false,
}) => {
  const { clientTheme } = useClientTheme();
  const [files, setFiles] = useState<FileWithType[]>([]);
  const [globalError, setGlobalError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Función para subir archivo al servidor
  const uploadFile = useCallback(
    async (fileWithType: FileWithType): Promise<string | null> => {
      if (!cuit) {
        throw new Error(BULK_UPLOAD_ERROR_MESSAGES.CUIT_REQUIRED);
      }

      const file = fileWithType.file;
      const fileName = removeFileExtension(file.name);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', fileWithType.type || '');
      formData.append('fileName', fileName);
      formData.append('isBasicData', 'false');

      // Si hay un callback personalizado, usarlo; si no, usar el servicio por defecto
      if (onFileUpload) {
        const result = await onFileUpload(
          file,
          fileWithType.type || '',
          fileWithType.label || ''
        );
        if (result && result.success && result.data) {
          return result.data;
        }
        throw new Error(
          result?.message || BULK_UPLOAD_ERROR_MESSAGES.FILE_UPLOAD_ERROR
        );
      } else {
        const response: any = await saveClientDocument(cuit, formData);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(BULK_UPLOAD_ERROR_MESSAGES.FILE_UPLOAD_ERROR);
      }
    },
    [cuit, onFileUpload]
  );

  // Manejar selección de archivos
  const handleFilesSelected = useCallback(
    (selectedFiles: FileList | null) => {
      if (!selectedFiles || selectedFiles.length === 0) return;

      setGlobalError('');
      const newFiles: FileWithType[] = [];

      Array.from(selectedFiles).forEach((file) => {
        // Validar archivo
        const validation = validateFile(
          file,
          maxFileSize,
          acceptedFormats,
          acceptedExtensions
        );
        if (!validation.isValid) {
          setGlobalError(validation.error || 'Error de validación');
          return;
        }

        // Validar duplicados
        const duplicateValidation = validateDuplicateFiles(
          files,
          file.name
        );
        if (!duplicateValidation.isValid) {
          setGlobalError(duplicateValidation.error || 'Archivo duplicado');
          return;
        }

        // Crear objeto FileWithType
        const fileWithType: FileWithType = {
          file,
          id: generateFileId(),
          type: null,
          fileName: file.name,
          fileSize: file.size,
          uploadStatus: 'pending' as UploadStatus,
        };

        newFiles.push(fileWithType);
      });

      if (newFiles.length > 0) {
        setFiles((prev) => [...prev, ...newFiles]);
      }
    },
    [files, maxFileSize, acceptedFormats, acceptedExtensions]
  );

  // Manejar cambio de tipo de documento
  const handleTypeChange = useCallback(
    async (fileId: string, typeLabel: string) => {
      const type = documentTypes.find((dt) => dt.label === typeLabel);
      if (!type) return;

      setFiles((prev) => {
        const fileToUpload = prev.find((f) => f.id === fileId);
        if (!fileToUpload) return prev;

        // Si deferredUpload es true, solo actualizar el tipo sin subir
        if (deferredUpload) {
          const updatedFiles = prev.map((f) => {
            if (f.id === fileId) {
              return { ...f, type: type.id, uploadStatus: 'pending' as UploadStatus };
            }
            return f;
          });
          onFilesUploaded?.(updatedFiles);
          return updatedFiles;
        }

        // Si deferredUpload es false, comportamiento original (subir inmediatamente)
        // Actualizar estado a uploading
        const updatedFiles: FileWithType[] = prev.map((f) => {
          if (f.id === fileId) {
            return {
              ...f,
              type: type.id,
              label: type.label,
              uploadStatus: 'uploading' as UploadStatus,
            };
          }
          return f;
        });

        // Subir archivo
        uploadFile({
          ...fileToUpload,
          type: type.id,
        })
          .then((uploadedFileName) => {
            setFiles((currentFiles) => {
              const finalFiles: FileWithType[] = currentFiles.map((f) => {
                if (f.id === fileId) {
                  return {
                    ...f,
                    type: type.id,
                    label: type.label,
                    uploadStatus: 'success' as UploadStatus,
                    uploadedFileName: uploadedFileName || undefined,
                    error: undefined,
                  };
                }
                return f;
              });

              // Notificamos al padre con todos los archivos (incluyendo el recién subido)
              onFilesUploaded?.(finalFiles);

              // Eliminamos de la lista interna los archivos que ya se subieron con éxito
              const pendingOrError: FileWithType[] = finalFiles.filter(
                (f) => f.uploadStatus !== 'success'
              );
              return pendingOrError;
            });
          })
          .catch((error) => {
            // Determinar tipo de error para mostrar mensaje apropiado
            let errorMessage: string;
            if (error instanceof Error) {
              const errorMsgLower = error.message.toLowerCase();
              // Detectar errores de conexión/red
              if (
                errorMsgLower.includes('network') ||
                errorMsgLower.includes('conexión') ||
                errorMsgLower.includes('connection') ||
                errorMsgLower.includes('timeout') ||
                errorMsgLower.includes('failed to fetch') ||
                errorMsgLower.includes('networkerror') ||
                errorMsgLower.includes('fetch')
              ) {
                errorMessage = BULK_UPLOAD_ERROR_MESSAGES.CONNECTION_ERROR;
              } else {
                // Error general de carga
                errorMessage = BULK_UPLOAD_ERROR_MESSAGES.UPLOAD_FAILED;
              }
            } else {
              errorMessage = BULK_UPLOAD_ERROR_MESSAGES.UPLOAD_FAILED;
            }

            setFiles((currentFiles) =>
              currentFiles.map((f) => {
                if (f.id === fileId) {
                  return {
                    ...f,
                    uploadStatus: 'error',
                    error: errorMessage,
                  };
                }
                return f;
              })
            );

            onError?.(errorMessage);
          });

        return updatedFiles;
      });
    },
    [documentTypes, uploadFile, onFilesUploaded, onError, deferredUpload]
  );

  // Eliminar archivo
  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    setGlobalError('');
  }, []);

  // Manejar drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      handleFilesSelected(droppedFiles);
    },
    [disabled, handleFilesSelected]
  );

  // Manejar click en área de drop
  const handleAreaClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // Manejar cambio de input file
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFilesSelected(e.target.files);
      // Resetear input para permitir seleccionar el mismo archivo nuevamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFilesSelected]
  );

  // Obtener título del dropdown para un archivo
  const getDropdownTitle = (file: FileWithType): string => {
    if (file.type) {
      const type = documentTypes.find((dt) => dt.id === file.type);
      return type?.label || 'Seleccioná el tipo de documento';
    }
    return 'Seleccioná el tipo de documento';
  };

  // Convertir documentTypes a formato de DropDown
  const dropdownItems = documentTypes.map((dt) => ({
    label: dt.label,
    disabled: dt.disabled,
  }));

  // Efecto para validar archivos sin tipo y mostrar error
  useEffect(() => {
    // Validar si hay archivos sin tipo asignado
    const hasFilesWithoutType = files.some((f) => !f.type && f.uploadStatus !== 'error');
    
    if (hasFilesWithoutType && files.length > 0) {
      setGlobalError(BULK_UPLOAD_ERROR_MESSAGES.SELECT_LABEL);
      onError?.(BULK_UPLOAD_ERROR_MESSAGES.SELECT_LABEL);
    } else {
      setGlobalError('');
    }
    
    onValidationChange?.(hasFilesWithoutType && files.length > 0);
  }, [files, onError, onValidationChange]);

  return (
    <BulkUploaderWrapper>
      {files.map((file) => (
        <Card key={file.id}>
          <Card margin={{ top: '3' }}>
            <FileRowContainer>
              <Icon variant="neutral" name="document_outline" />
              <DropdownWrapper>
                <DropDown
                  id={`document-type-${file.id}`}
                  items={dropdownItems}
                  size="md"
                  title={getDropdownTitle(file)}
                  onChange={(label) => handleTypeChange(file.id, label)}
                  disabled={disabled || file.uploadStatus === 'uploading'}
                  variant="instrumentation"
                />
              </DropdownWrapper>
              <FileNameWrapper>
                <Card
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  variant="neutral"
                  modifier="inverted"
                  height="46px"
                  alignItems="center"
                  spacing={{ left: '2', right: '2' }}
                  borderRadiusAmount="3"
                  border
                  contain="content"
                >
                  <FileNameLabelWrapper>
                    <TruncatedTextWithTooltip text={file.fileName}>
                      <Label variant="neutral" modifier="strong" type="2">
                        {file.fileName}
                      </Label>
                    </TruncatedTextWithTooltip>
                  </FileNameLabelWrapper>
                  {file.uploadStatus === 'uploading' ? (
                    <Spinner size="sm" variant="primary" />
                  ) : (
                  <Icon
                    name="delete_outline"
                    variant="neutral"
                    height={44}
                    cursor="pointer"
                    onClick={() => handleRemoveFile(file.id)}
                    disabled={disabled}
                  />
                  )}
                </Card>
              </FileNameWrapper>
            </FileRowContainer>
          </Card>
          {(file.error || file.uploadStatus === 'error') && (
            <Card margin={{ top: '1', left: '6' }}>
              <Span variant="danger" type="2">
                {file.error || 'Error al subir el archivo'}
              </Span>
            </Card>
          )}
        </Card>
      ))}

      <DragAndDropArea
        disabled={disabled}
        hasError={!!globalError}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleAreaClick}
        style={{ marginTop: files.length > 0 ? '40px' : '0' }}
        useTheme={!!clientTheme}
      >
        <FileInput
          ref={fileInputRef}
          type="file"
          multiple
          accept={[...acceptedFormats, ...acceptedExtensions].join(',')}
          onChange={handleInputChange}
          disabled={disabled}
        />
        <DropBaseIcon useTheme={!!clientTheme}>
          <DropBaseIconWrapper useTheme={!!clientTheme}>
            <Icon
              name="upload_cloud_outline"
              variant={clientTheme ? 'primary' : 'success'}
              modifier="strong"
              height={32}
              width={32}
            />
          </DropBaseIconWrapper>
        </DropBaseIcon>
        <MainTextWrapper>
          <P withMargin={false} type="1" variant={clientTheme ? 'primary' : 'success'} weight="bold">
            Hacé click para cargar
          </P>
          <P withMargin={false} type="1" variant="neutral">
            o arrastrá y soltá
          </P>
          <P withMargin={false} type="2" variant="neutral" align="center">
            PDF (Peso máximo {Math.round(maxFileSize / (1024 * 1024))} MB)
          </P>
        </MainTextWrapper>
      </DragAndDropArea>

      {globalError && <GlobalError>{globalError}</GlobalError>}
    </BulkUploaderWrapper>
  );
};

export default BulkDocumentUploader;

